import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { apiUrl } from "../utils/api";
import "../styles/Products.css";

const PDFJS_VERSION = "3.11.174";
const PDFJS_URL = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.min.js`;
const PDFJS_WORKER_URL = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.worker.min.js`;

const loadPdfJs = () => {
  if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);

  return new Promise((resolve, reject) => {
    const existingScript = document.querySelector(`script[src="${PDFJS_URL}"]`);

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(window.pdfjsLib), {
        once: true,
      });
      existingScript.addEventListener("error", reject, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = PDFJS_URL;
    script.async = true;
    script.onload = () => resolve(window.pdfjsLib);
    script.onerror = reject;
    document.body.appendChild(script);
  });
};

const ProductCatalogue = () => {
  const catalogueViewUrl = apiUrl("/catalogue/view");
  const catalogueDownloadUrl = apiUrl("/catalogue/download");
  const viewerRef = useRef(null);
  const pageRefs = useRef([]);
  const renderTasksRef = useRef([]);
  const renderedPagesRef = useRef(new Set());
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageNumbers, setPageNumbers] = useState([]);
  const [status, setStatus] = useState("Loading catalogue...");
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadCatalogue = async () => {
      setStatus("Loading catalogue...");
      setLoadError("");

      try {
        const pdfjsLib = await loadPdfJs();
        if (cancelled) return;

        pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL;

        const loadingTask = pdfjsLib.getDocument({
          url: catalogueViewUrl,
          withCredentials: false,
          disableAutoFetch: true,
        });
        const loadedPdf = await loadingTask.promise;

        if (cancelled) {
          loadedPdf.destroy();
          return;
        }

        setPdfDoc(loadedPdf);
        setPageNumbers(Array.from({ length: loadedPdf.numPages }, (_, i) => i + 1));
        setStatus("");
      } catch (err) {
        console.error("Catalogue load error:", err);
        if (!cancelled) {
          setLoadError("Catalogue preview could not load. Please download the PDF instead.");
          setStatus("");
        }
      }
    };

    loadCatalogue();

    return () => {
      cancelled = true;
      renderTasksRef.current.forEach((task) => task?.cancel?.());
      renderTasksRef.current = [];
    };
  }, [catalogueViewUrl]);

  useEffect(() => {
    if (!pdfDoc || !pageNumbers.length) return undefined;

    let resizeTimer;
    const renderPage = async (pageNumber) => {
      if (renderedPagesRef.current.has(pageNumber)) return;

      const pageEl = pageRefs.current[pageNumber - 1];
      const canvas = pageEl?.querySelector("canvas");
      if (!pageEl || !canvas) return;

      renderedPagesRef.current.add(pageNumber);
      pageEl.classList.add("is-rendering");

      try {
        const page = await pdfDoc.getPage(pageNumber);
        const availableWidth = Math.max(
          280,
          Math.floor((viewerRef.current?.clientWidth || 900) - 24)
        );
        const initialViewport = page.getViewport({ scale: 1 });
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const scale = availableWidth / initialViewport.width;
        const viewport = page.getViewport({ scale });

        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;

        const context = canvas.getContext("2d", { alpha: false });
        context.setTransform(dpr, 0, 0, dpr, 0, 0);

        const renderTask = page.render({ canvasContext: context, viewport });
        renderTasksRef.current.push(renderTask);
        await renderTask.promise;
        pageEl.classList.remove("is-rendering");
        pageEl.classList.add("is-rendered");
      } catch (err) {
        if (err?.name !== "RenderingCancelledException") {
          console.error(`PDF page ${pageNumber} render error:`, err);
          pageEl?.classList.remove("is-rendering");
        }
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const pageNumber = Number(entry.target.dataset.pageNumber);
          renderPage(pageNumber);
          if (pageNumber < pageNumbers.length) renderPage(pageNumber + 1);
        });
      },
      { rootMargin: "700px 0px" }
    );

    pageRefs.current.forEach((pageEl) => {
      if (pageEl) observer.observe(pageEl);
    });

    renderPage(1);
    if (pageNumbers.length > 1) renderPage(2);

    const handleResize = () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        renderTasksRef.current.forEach((task) => task?.cancel?.());
        renderTasksRef.current = [];
        renderedPagesRef.current = new Set();
        pageRefs.current.forEach((pageEl) => {
          pageEl?.classList.remove("is-rendered", "is-rendering");
        });
        renderPage(1);
      }, 180);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", handleResize);
      window.clearTimeout(resizeTimer);
    };
  }, [pdfDoc, pageNumbers]);

  return (
    <main className="catalogue-view-page">
      <div className="catalogue-view-header">
        <div>
          <p className="catalogue-view-kicker">Product Catalogue</p>
          <h1>Krrish Impex Catalogue</h1>
        </div>
        <div className="catalogue-view-actions">
          <Link to="/products" className="catalogue-view-secondary">
            Back to Products
          </Link>
          <a
            href={catalogueDownloadUrl}
            className="catalogue-view-primary"
            download="Krrish-Impex-Catalogue.pdf"
          >
            Download PDF
          </a>
        </div>
      </div>

      <section className="catalogue-viewer" ref={viewerRef} aria-label="Catalogue PDF preview">
        {status && <div className="catalogue-view-status">{status}</div>}
        {loadError && (
          <div className="catalogue-view-error">
            <p>{loadError}</p>
            <a href={catalogueViewUrl} target="_blank" rel="noreferrer">
              Open PDF in browser
            </a>
          </div>
        )}
        {pageNumbers.map((pageNumber) => (
          <div
            className="catalogue-page"
            data-page-number={pageNumber}
            key={pageNumber}
            ref={(node) => {
              pageRefs.current[pageNumber - 1] = node;
            }}
          >
            <canvas aria-label={`Catalogue page ${pageNumber}`} />
            <span className="catalogue-page-number">
              {pageNumber} / {pageNumbers.length}
            </span>
          </div>
        ))}
      </section>
    </main>
  );
};

export default ProductCatalogue;
